import {
  APPOINTMENT_CHECKLIST_ITEM_TYPE_CONFIG,
  AppointmentChecklistAttachedPhotoSetting,
  AppointmentChecklistInstanceItem,
  AppointmentChecklistItemType,
  bzExpect,
  isNullish,
  JobGuid,
  R,
} from '@breezy/shared'
import { faCheck, faClose, faFlag, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Form, Input, Radio } from 'antd'
import cn from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SignaturePad from 'react-signature-canvas'
import { EmDash } from '../../elements/EmDash/EmDash'
import { HtmlRenderer } from '../../elements/HtmlRenderer/HtmlRenderer'
import useIsMobile from '../../hooks/useIsMobile'
import { OnResize, useResizeObserver } from '../../hooks/useResizeObserver'
import { OnsiteConfirmModal, OnsiteModal, OnsiteModalContent } from '../../ui/OnsiteModal/OnsiteModal'
import { StateSetter, stopPropagation, useModalState } from '../../utils/react-utils'
import { isPhotoUploading, isUploadedPhoto, LoadingImage, LoadingImageData } from '../Upload/LoadingImage'
import { WysiwygEditor } from '../WysiwygEditor/WysiwygEditor'
import { ChecklistPhotoUploadModal, PhotoSelector } from './ChecklistPhotoUploadModal'

const MAX_SIGNATURE_WIDTH = 500
const SIGNATURE_ASPECT_RATIO = 2.5

const IMAGE_THUMBNAIL_WIDTH = 200

type EmbeddedImageProps = {
  name: string
  photoData: LoadingImageData
  onDelete?: () => void
}

// Thumbnail (and enlarged modal) of an attached photo. Usable for both photo inputs and associated photos.
const EmbeddedImage = React.memo<EmbeddedImageProps>(({ name, photoData, onDelete }) => {
  const [enlargedOpen, openEnlarged, closeEnlarged] = useModalState()
  const [onDeleteWarningOpen, openOnDeleteWarning, closeOnDeleteWarning] = useModalState()

  const src = useMemo(() => (isUploadedPhoto(photoData) ? photoData.base64File : photoData.cdnUrl), [photoData])
  return (
    <>
      <div className="relative mt-4 inline-block">
        <LoadingImage
          image={photoData}
          onClick={openEnlarged}
          className="cursor-pointer rounded border border-solid border-bz-gray-500"
          width={IMAGE_THUMBNAIL_WIDTH}
        />
        {/* Overlaid delete button */}
        {onDelete ? (
          <span onClick={stopPropagation}>
            <div
              className="absolute right-1 top-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-solid border-bz-gray-100 bg-bz-gray-800 text-xs text-bz-gray-100"
              onClick={openOnDeleteWarning}
            >
              <FontAwesomeIcon icon={faClose} />
            </div>
          </span>
        ) : null}
      </div>

      {enlargedOpen && (
        <OnsiteModal size="large" onClose={closeEnlarged}>
          <OnsiteModalContent header={name} onClose={closeEnlarged}>
            <div className="flex flex-col space-y-4">
              <img src={src} alt={name} onClick={openEnlarged} />
              {onDelete ? (
                <Button danger onClick={openOnDeleteWarning}>
                  Delete
                </Button>
              ) : null}
            </div>
          </OnsiteModalContent>
        </OnsiteModal>
      )}
      {onDeleteWarningOpen && (
        <OnsiteConfirmModal
          danger
          header="Are you sure you want to remove this photo?"
          onCancel={closeOnDeleteWarning}
          onConfirm={onDelete}
          confirmText="Yes, Remove Photo"
        >
          Are you sure you want to remove this photo from the form?
        </OnsiteConfirmModal>
      )}
    </>
  )
})

type SignatureFieldProps = {
  value?: string
  onChange: (base64ImageUri: string) => void
}

const SignatureField = React.memo<SignatureFieldProps>(({ value, onChange }) => {
  const signaturePadRef = useRef<SignaturePad | null>(null)

  // We need a ref for the container to get the size so we know how big to make the signature pad.
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const onResize = useCallback<OnResize>(({ width }) => {
    setContainerWidth(width)
  }, [])
  useResizeObserver(containerRef, onResize)

  const [signaturePadWidth, signaturePadHeight] = useMemo(() => {
    const width = Math.min(containerWidth, MAX_SIGNATURE_WIDTH)
    const height = width / SIGNATURE_ASPECT_RATIO
    return [width, height]
  }, [containerWidth])

  // If the size of the signature pad changes and we have a pre-existing value passed in, we need to reload it
  // (otherwise the value won't be scaled to the right size). This doubles as the initial load.
  // biome-ignore lint/correctness/useExhaustiveDependencies: value dependency intentionally omitted to prevent reload on value changes
  useEffect(() => {
    if (value && signaturePadWidth) {
      // Clear out what's in there
      signaturePadRef.current?.clear()
      // Write the value (the data for the signature image) to the pad with the new size.
      signaturePadRef.current?.fromDataURL(value ?? '', {
        width: signaturePadWidth,
        height: signaturePadHeight,
      })
    }
    // I don't want to reload this when the value changes, other than the first time we load (or when the size changes
    // and we have to reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signaturePadWidth, signaturePadHeight])

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear()
    onChange('')
  }, [onChange])

  const onDrawEnd = useCallback(() => {
    const base64ImageUri = bzExpect(signaturePadRef.current?.toDataURL('image/png'), 'The signature pad should exist')
    onChange(base64ImageUri)
  }, [onChange])

  return (
    <div ref={containerRef} style={{ maxWidth: `${MAX_SIGNATURE_WIDTH}px` }}>
      <SignaturePad
        ref={signaturePadRef}
        onEnd={onDrawEnd}
        clearOnResize={false}
        canvasProps={{
          height: signaturePadHeight,
          width: signaturePadWidth,
          className: 'border-2 border-solid border-bz-gray-500 rounded',
        }}
      />
      <Button
        block
        size="large"
        type="link"
        className={cn('mt-2 font-semibold text-bz-primary', {
          'opacity-[0.36]': !value,
        })}
        onClick={clearSignature}
      >
        Clear Signature
      </Button>
    </div>
  )
})

// Common UI wrapping the various input items
type ChecklistInputItemWrapperProps = React.PropsWithChildren<{
  // ReactNode to show when the item is `readonly`. If undefined, we render the value as a string.
  readonlyElement?: React.ReactNode
  // If true, we don't add `mb-6` to the bottom
  noMargin?: boolean
  name: string
  required?: boolean
  readonly?: boolean
  value?: string
  helperText?: string
  associatedPhotoSetting?: AppointmentChecklistAttachedPhotoSetting
  onAttachPhoto: () => void
  onDeletePhoto: () => void
  // This photo data can be fetched data (just the cdn URL) or all the loading data. If `undefined`, then there is no
  // image.
  photoData?: LoadingImageData | undefined
}>

const ChecklistInputItemWrapper = React.memo<ChecklistInputItemWrapperProps>(
  ({
    children,
    readonlyElement,
    noMargin,
    name,
    required,
    readonly,
    value,
    helperText,
    associatedPhotoSetting,
    onAttachPhoto,
    onDeletePhoto,
    photoData,
  }) => (
    <Form.Item className={cn({ 'mb-6': !noMargin })} label={name} required={required}>
      {helperText && <div className="mb-2 mt-[-8px] text-sm text-bz-gray-700">{helperText}</div>}
      {readonly ? (readonlyElement ?? <div className="text-base font-semibold">{value || <EmDash />}</div>) : children}
      {associatedPhotoSetting && associatedPhotoSetting !== 'NONE' ? (
        <div>
          {photoData ? (
            <EmbeddedImage name={name} photoData={photoData} onDelete={readonly ? undefined : onDeletePhoto} />
          ) : readonly ? undefined : (
            <Button type="link" className="px-0 py-2" onClick={onAttachPhoto}>
              Attach a photo ({associatedPhotoSetting === 'OPTIONAL' ? 'Optional' : 'Required'})
            </Button>
          )}
        </div>
      ) : null}
    </Form.Item>
  ),
)

type ChecklistInputItemProps = {
  name: string
  type: AppointmentChecklistItemType
  value: string
  notRequired?: boolean
  onChange: (value: string) => void
  readonly: boolean
  helperText?: string
  associatedPhotoSetting?: AppointmentChecklistAttachedPhotoSetting
  onAttachPhoto: () => void
  onDeletePhoto: () => void
  photoData?: LoadingImageData | undefined
}

const ChecklistInputItem = React.memo<ChecklistInputItemProps>(
  ({
    name,
    type,
    value,
    onChange,
    notRequired,
    readonly,
    onAttachPhoto,
    onDeletePhoto,
    photoData,
    ...wrapperPassthrough
  }) => {
    // Don't show any required UI if we're readonly
    const ourRequired = readonly ? false : !notRequired

    const wrapperProps = useMemo<Omit<ChecklistInputItemWrapperProps, 'children'>>(
      () => ({
        name,
        required: ourRequired,
        readonly,
        value,
        onAttachPhoto,
        onDeletePhoto,
        photoData,
        ...wrapperPassthrough,
      }),
      // biome-ignore lint/correctness/useExhaustiveDependencies: wrapperPassthrough changes on every re-render but contains stable values
      [name, ourRequired, readonly, value, onAttachPhoto, onDeletePhoto, photoData, wrapperPassthrough],
    )

    switch (type) {
      case 'CHECKBOX':
        return (
          <ChecklistInputItemWrapper {...wrapperProps}>
            <Radio.Group value={value} onChange={e => onChange(e.target.value)} optionType="button">
              {APPOINTMENT_CHECKLIST_ITEM_TYPE_CONFIG.CHECKBOX.options.map(option => (
                <Radio key={option} value={option}>
                  {option}
                </Radio>
              ))}
            </Radio.Group>
          </ChecklistInputItemWrapper>
        )

      case 'TEXT':
        return (
          <ChecklistInputItemWrapper
            {...wrapperProps}
            readonlyElement={
              value ? (
                <div className="border border-solid border-bz-gray-500 p-4">
                  <HtmlRenderer htmlContent={value} />
                </div>
              ) : (
                <EmDash />
              )
            }
          >
            <WysiwygEditor value={value} onChange={onChange} />
          </ChecklistInputItemWrapper>
        )
      case 'INPUT':
        return (
          <ChecklistInputItemWrapper {...wrapperProps}>
            <Input value={value} onChange={e => onChange(e.target.value)} />
          </ChecklistInputItemWrapper>
        )

      case 'PASS_FAIL_FLAG': {
        // We show very similar UI for readonly and editable.
        const element = (
          <div className="flex flex-row space-x-2">
            {(
              [
                ['Pass', faCheck, 'border-bz-green-600', 'bg-bz-green-100', 'text-bz-green-800'],
                ['Flag', faFlag, 'border-bz-orange-600', 'bg-bz-orange-100', 'text-bz-orange-800'],
                ['Fail', faClose, 'border-bz-red-600', 'bg-bz-red-100', 'text-bz-red-800'],
              ] satisfies [
                (typeof APPOINTMENT_CHECKLIST_ITEM_TYPE_CONFIG.PASS_FAIL_FLAG.options)[number],
                IconDefinition,
                string,
                string,
                string,
              ][]
            ).map(([label, icon, borderColor, bgColor, selectedLabelColor]) => {
              const isSelected = value === label
              if (readonly && !isSelected) {
                return null
              }
              return (
                <Button
                  key={label}
                  className={cn(`flex min-w-0 max-w-[88px] items-center justify-center gap-[6px] rounded-sm`, {
                    [selectedLabelColor]: isSelected,
                    'flex-1': !readonly,
                    'text-black': !isSelected,
                    [bgColor]: isSelected,
                    'bg-transparent': !isSelected,
                    [borderColor]: isSelected,
                    'border-bz-gray-500': !isSelected,
                  })}
                  onClick={() => {
                    if (readonly) {
                      return
                    }
                    if (isSelected) {
                      onChange('')
                    } else {
                      onChange(label)
                    }
                  }}
                >
                  <FontAwesomeIcon icon={icon} className={cn('h-4 w-4', selectedLabelColor)} />
                  {label}
                </Button>
              )
            })}
          </div>
        )

        return (
          <ChecklistInputItemWrapper {...wrapperProps} noMargin readonlyElement={element}>
            {element}
          </ChecklistInputItemWrapper>
        )
      }
      case 'YES_NO':
        return (
          <ChecklistInputItemWrapper {...wrapperProps}>
            <Radio.Group value={value} onChange={e => onChange(e.target.value)} optionType="button">
              {APPOINTMENT_CHECKLIST_ITEM_TYPE_CONFIG.YES_NO.options.map(option => (
                <Radio key={option} value={option}>
                  {option}
                </Radio>
              ))}
            </Radio.Group>
          </ChecklistInputItemWrapper>
        )
      case 'SIGNATURE':
        return (
          <ChecklistInputItemWrapper
            {...wrapperProps}
            readonlyElement={
              value ? (
                <div className="rounded border border-solid border-bz-gray-500 p-4">
                  <img src={value} alt={name} />
                </div>
              ) : (
                <EmDash />
              )
            }
          >
            <SignatureField value={value} onChange={onChange} />
          </ChecklistInputItemWrapper>
        )
      case 'PHOTO': {
        const imageElement =
          (photoData ?? value) ? (
            <EmbeddedImage
              name={name}
              photoData={photoData ?? { cdnUrl: value }}
              onDelete={readonly ? undefined : onDeletePhoto}
            />
          ) : null
        return (
          <ChecklistInputItemWrapper {...wrapperProps} readonlyElement={imageElement ?? null}>
            {imageElement ?? <Button onClick={onAttachPhoto}>Upload Photo</Button>}
          </ChecklistInputItemWrapper>
        )
      }
    }
  },
)

// For the upload state data map, we can use this to make a unique, deterministic key from the JSON
const makePhotoStateKey = (photoSelector: PhotoSelector) =>
  `${photoSelector.itemIndex}-${photoSelector.side ?? ''}-${photoSelector.rowIndex ?? ''}`

type AppointmentChecklistInstanceFormProps = {
  items: AppointmentChecklistInstanceItem[]
  setItems?: StateSetter<AppointmentChecklistInstanceItem[]>
  setIsDirty?: (value: boolean) => void
  // Can be used to block saving or canceling if we are still uploading a photo
  setIsPhotoUploading?: (value: boolean) => void
  className?: string
  jobGuid?: JobGuid
}

export const AppointmentChecklistInstanceForm = React.memo<AppointmentChecklistInstanceFormProps>(
  ({ items, setItems, setIsDirty, setIsPhotoUploading, className, jobGuid }) => {
    // Which selector we're currently uploading a photo for. If `undefined`, we shouldn't have the selection modal
    // open.
    const [uploadingPhotoFor, setUploadingPhotoFor] = useState<PhotoSelector>()

    // The list won't change order, so the position is a sufficient key. Also, photo input and associated photo are
    // mutually exclusive (you can't have a photo input that has an associated photo). So photo data a given position
    // is either for the photo input or the associated photo based on the input type.
    const [photoUploadingStateMap, setPhotoUploadingStateMap] = useState<Record<string, LoadingImageData | undefined>>(
      {},
    )

    const hasSetIsPhotoUploading = !!setIsPhotoUploading

    // biome-ignore lint/correctness/useExhaustiveDependencies: setIsPhotoUploading dependency intentionally managed via hasSetIsPhotoUploading to avoid infinite loops
    useEffect(() => {
      if (!setIsPhotoUploading) {
        return
      }
      for (const photoData of R.values(photoUploadingStateMap)) {
        if (!photoData) {
          continue
        }
        // It only takes one photo in the entire form to consider the whole thing uploading. We don't need to do any
        // nested dual-column checking because the map is taking that into account with the keys.
        if (isPhotoUploading(photoData)) {
          setIsPhotoUploading(true)
          return
        }
      }
      // If we didn't see anything loading, then we aren't loading
      setIsPhotoUploading(false)
      // We have a situation where this `useEffect` isn't resilient against an anonymous `setIsPhotoUploading`
      // function being passed. It will infinitely loop because it will constantly call the function, which will
      // change the shallow equality of the function. The only time we want to update the parent about the upload
      // state is when the upload state changes, or we go from not having a setter function to have one (if we newly
      // get a function, we need to call it with the latest value).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photoUploadingStateMap, hasSetIsPhotoUploading])

    const updatePhotoUploadingState = useCallback(
      (photoSelector: PhotoSelector, photoData: LoadingImageData | undefined) => {
        setPhotoUploadingStateMap(prevState => ({
          ...prevState,
          [makePhotoStateKey(photoSelector)]: photoData,
        }))
      },
      [],
    )

    // Updating a photo is more complicated than updating a simple form input value.
    const updatePhoto = useCallback(
      (photoSelector: PhotoSelector, photoData: LoadingImageData | undefined) => {
        if (!photoSelector || !setItems) {
          return
        }
        // Update the map that's keeping track of our uploading states
        updatePhotoUploadingState(photoSelector, photoData)

        setItems(items => {
          const { itemIndex, side, rowIndex } = photoSelector

          const existingItem = items[itemIndex]

          if (!existingItem) {
            console.error(`Tried to upload photo for item at index ${itemIndex}, but it does not exist.`)
            return items
          }
          if (existingItem.type === 'PHOTO') {
            // If the cdnUrl is an empty string, then we haven't finished that part of the upload, and we don't want to
            // save it. Otherwise, it's either a valid URL or `photoData` is undefined (which is a deletion) and we want
            // to save it as `undefined`.
            if (photoData?.cdnUrl !== '') {
              return R.adjust(
                itemIndex,
                item => ({
                  ...item,
                  response: photoData?.cdnUrl,
                }),
                items,
              )
            }
          } else if (existingItem.type === 'DUAL_SECTION') {
            if (isNullish(side)) {
              console.error(
                `Tried to upload photo for dual section item at index ${itemIndex}, but side is not defined.`,
              )
              return items
            }
            if (isNullish(rowIndex)) {
              console.error(
                `Tried to upload photo for dual section item at index ${itemIndex}, but rowIndex is not defined.`,
              )
              return items
            }

            // If the cdnUrl is an empty string, then we haven't finished that part of the upload, and we don't want to
            // save it. Otherwise, it's either a valid URL or `photoData` is undefined (which is a deletion) and we want
            // to save it as `undefined`.
            if (photoData?.cdnUrl !== '') {
              const updatedSection = {
                ...existingItem,
                [side]: R.adjust(
                  rowIndex,
                  item => {
                    if (item.type === 'PHOTO') {
                      return { ...item, response: photoData?.cdnUrl }
                    } else {
                      return {
                        ...item,
                        associatedPhotoCdnUrl: photoData?.cdnUrl,
                      }
                    }
                  },
                  existingItem[side],
                ),
              }
              return R.update(itemIndex, updatedSection, items)
            }
          } else {
            // If the cdnUrl is an empty string, then we haven't finished that part of the upload, and we don't want to
            // save it. Otherwise, it's either a valid URL or `photoData` is undefined (which is a deletion) and we want
            // to save it as `undefined`.
            if (photoData?.cdnUrl !== '') {
              return R.adjust(
                itemIndex,
                item => ({
                  ...item,
                  associatedPhotoCdnUrl: photoData?.cdnUrl,
                }),
                items,
              )
            }
          }

          return items
        })

        setIsDirty?.(true)
      },
      [setIsDirty, setItems, updatePhotoUploadingState],
    )

    const onPhotoUploadChange = useCallback(
      (photoSelector: PhotoSelector, photoData: LoadingImageData) => {
        // If we have the photo upload modal open, and we get an event for that photo, close the modal. We want to
        // close the modal when they start uploading. But they can upload another photo while the first one is loading
        // (that scoped instance of the function will still be calling the scoped instance of this function).
        if (R.equals(uploadingPhotoFor, photoSelector)) {
          setUploadingPhotoFor(uploadingPhotoFor => {
            if (R.equals(uploadingPhotoFor, photoSelector)) {
              return undefined
            }
            return uploadingPhotoFor
          })
        }
        updatePhoto(photoSelector, photoData)
      },
      [updatePhoto, uploadingPhotoFor],
    )
    const onPhotoDeleted = useCallback(
      (photoSelector: PhotoSelector) => {
        updatePhoto(photoSelector, undefined)
      },
      [updatePhoto],
    )

    const updateItem = useCallback(
      (index: number, value: string) => {
        setItems?.(items => R.adjust(index, item => ({ ...item, response: value }), items))
        setIsDirty?.(true)
      },
      [setItems, setIsDirty],
    )

    const updateDualColumnItem = useCallback(
      (itemIndex: number, side: 'left' | 'right', rowIndex: number, value: string) => {
        setItems?.(items => {
          const section = items[itemIndex]
          if (section.type !== 'DUAL_SECTION') {
            console.error(`Item at index ${itemIndex} is not a dual section`)
            return items
          }
          const column = section[side]
          const newColumn = R.adjust(rowIndex, item => ({ ...item, response: value }), column)
          const newSection = {
            ...section,
            [side]: newColumn,
          }
          return items.toSpliced(itemIndex, 1, newSection)
        })
        setIsDirty?.(true)
      },
      [setIsDirty, setItems],
    )

    const isMobile = useIsMobile()

    const readonly = !setItems
    return (
      <div className={className}>
        <Form layout="vertical" className="min-h-0 flex-1 space-y-6 *:mb-0">
          {items.map((item, i) => {
            if (item.type === 'TITLE') {
              return (
                // i is generally a bad key, but this list won't change order
                <div key={i} className="border-0 border-b border-solid border-bz-gray-500 text-xl font-semibold">
                  {item.name}
                </div>
              )
            }

            if (item.type === 'DUAL_SECTION') {
              const maxRows = Math.max(item.left.length, item.right.length)
              return (
                <div
                  // i is generally a bad key, but this list won't change order
                  key={i}
                  className={cn('grid *:mb-0', isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-2 gap-x-2 gap-y-6')}
                >
                  {R.range(0, maxRows).map(rowIndex => (
                    // i is generally a bad key, but this list won't change order
                    <React.Fragment key={rowIndex}>
                      {(['left', 'right'] as const).map(side => {
                        const ourItem = item[side][rowIndex]
                        const photoSelector: PhotoSelector = {
                          itemIndex: i,
                          side,
                          rowIndex,
                        }
                        const existingCdnUrl =
                          ourItem?.type === 'PHOTO' ? ourItem?.response : ourItem?.associatedPhotoCdnUrl

                        return ourItem ? (
                          <ChecklistInputItem
                            key={side}
                            {...ourItem}
                            value={ourItem.response ?? ''}
                            onChange={value => updateDualColumnItem(i, side, rowIndex, value)}
                            readonly={readonly}
                            onAttachPhoto={() => setUploadingPhotoFor(photoSelector)}
                            onDeletePhoto={() => onPhotoDeleted(photoSelector)}
                            photoData={
                              photoUploadingStateMap[makePhotoStateKey(photoSelector)] ??
                              (existingCdnUrl ? { cdnUrl: existingCdnUrl } : undefined)
                            }
                          />
                        ) : // If there isn't an item for this side, we still render a div so the grid works. However, on
                        // mobile, we only have a one-col grid so just render a fragment with nothing in it.
                        isMobile ? (
                          <React.Fragment key={side} />
                        ) : (
                          <div key={side} />
                        )
                      })}
                    </React.Fragment>
                  ))}
                </div>
              )
            }

            const existingCdnUrl = item.type === 'PHOTO' ? item.response : item.associatedPhotoCdnUrl

            return (
              <ChecklistInputItem
                // i is generally a bad key, but this list won't change order
                key={i}
                {...item}
                value={item.response || ''}
                onChange={value => updateItem(i, value)}
                readonly={readonly}
                onAttachPhoto={() =>
                  setUploadingPhotoFor({
                    itemIndex: i,
                  })
                }
                onDeletePhoto={() => onPhotoDeleted({ itemIndex: i })}
                photoData={
                  photoUploadingStateMap[makePhotoStateKey({ itemIndex: i })] ??
                  (existingCdnUrl
                    ? {
                        cdnUrl: existingCdnUrl,
                      }
                    : undefined)
                }
              />
            )
          })}
        </Form>
        {uploadingPhotoFor && (
          <ChecklistPhotoUploadModal
            onClose={() => setUploadingPhotoFor(undefined)}
            photoSelector={uploadingPhotoFor}
            onPhotoUploadChange={onPhotoUploadChange}
            jobGuid={jobGuid}
          />
        )}
      </div>
    )
  },
)

AppointmentChecklistInstanceForm.displayName = 'AppointmentChecklistInstanceForm'
